<template>
	<div class="category-page">
		<!-- 顶部固定：返回 + 搜索 + 分类 Tab -->
		<div class="categoryHeadBox tab-fixed">
			<div class="top-head">
				<van-icon name="arrow-left" size="1.5rem" color="#86909c" @click="navigateBack()" class="back-icon" />
				<div class="search-head-cell">
					<NuxtLink :to="searchHref" class="category-search-link">
						<span class="category-search-placeholder">产品名称/型号规格/厂家/商品描述</span>
						<span class="category-search-button">搜索</span>
					</NuxtLink>
				</div>
			</div>
			<v-tabs
				v-model="currentVTabs"
				:tabs="TABS"
				:hrefs="tabHrefs"
				@change="changeTab"
				active-color="#455BD9"
				color="#595959"
				font-size="1.17rem"
				active-font-size="1.17rem"
				line-height="0.25rem"
				line-color="#455BD9"
				:line-scale="0.2"
				line-radius="0.13rem"
				height="2.8rem"
				padding="0"
				padding-item="0 1rem"
			/>
		</div>

		<!-- 主内容：左侧一级分类 + 右侧子分类网格 -->
		<div class="categoryBox">
			<div v-if="categoryPending && !leftCategories.length" class="category-loading">
				<van-loading type="spinner" color="#c0c4cc" />
			</div>
			<div v-else-if="!kongFlag" class="categoryBox-inner">
				<div class="treeTabs" ref="leftTabScrollRef">
					<CategoryTreeTab ref="treeTabRef" @orderTap="orderTap" :categories="leftCategories" />
				</div>
				<div class="rightTab" ref="rightTabRef">
					<div class="categoryListBox" v-show="leftCategories.length">
						<div
							v-for="(firstCategory, firstIndex) in leftCategories"
							v-show="isLeftCategoryVisible(firstCategory)"
							:key="firstCategory.categoryId || firstCategory.name || firstIndex"
							class="categoryPanel"
						>
							<div
								class="categoryBanner"
								v-for="(item, i) in firstCategory.subCategory || []"
								:key="item.categoryId || item.name || i"
							>
								<div class="categoryTitle">
									<span>{{ item.name }}</span>
								</div>
								<div class="categoryListLeftBox" v-show="item.subCategory?.length">
									<a
										v-for="(sub, j) in item.subCategory"
										:key="sub.categoryId || sub.name || j"
										:href="getProductListHref(sub, item)"
										class="categoryList"
										@click.prevent="toProductList(sub, item)"
									>
										<NuxtImg
											:src="categorySubImgSrc(sub, item, firstIndex, i, j)"
											class="category-img"
											:alt="sub.name"
											width="110"
											height="110"
											@error="onCategorySubImgError(sub, item, firstIndex, i, j)"
										/>
										<div class="categoryListTitle">{{ sub.name }}</div>
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<EmptyState v-else />
		</div>
		<footerBar v-if="footerType === 'main'" :checks="1" />
		<SubFoot v-if="footerType === 'child'" />
	</div>
</template>

<script setup>
import { ref, reactive, nextTick, computed, watch, onBeforeUnmount } from 'vue';
import { navigateBack } from '@/utils/utils';
import { getMobileCategoryFromSearch } from '@/api/index';
import { NATURE_MAP, NATURE_INDEX, TABS } from '#shared/classifyConstants';
import {
	buildCategoryDetailPath,
	buildCategoryPath,
	buildCategorySearchPath,
	normalizeCompanyId,
} from '#shared/categoryRoutes';
import CategoryTreeTab from './components/TreeTab.vue';

const CATEGORY_SUB_IMG_PLACEHOLDER = '/staticSeo/images/placeholder.png';

/** NuxtImg 内部用 :src 驱动真实地址，error 里只改 DOM 会被下一次渲染覆盖，需用响应式切换绑定 */
const failedCategorySubImgKeys = reactive({});

function categorySubImgFailKey(sub, item, firstIndex, i, j) {
	return `${firstIndex}-${i}-${j}-${item?.name ?? ''}::${sub?.name ?? ''}::${String(sub?.imageUrl ?? '').trim()}`;
}

function categorySubImgSrc(sub, item, firstIndex, i, j) {
	const key = categorySubImgFailKey(sub, item, firstIndex, i, j);
	if (failedCategorySubImgKeys[key]) {
		return CATEGORY_SUB_IMG_PLACEHOLDER;
	}
	const raw = sub.imageUrl != null ? String(sub.imageUrl).trim() : '';
	return raw || CATEGORY_SUB_IMG_PLACEHOLDER;
}

function onCategorySubImgError(sub, item, firstIndex, i, j) {
	const raw = sub.imageUrl != null ? String(sub.imageUrl).trim() : '';
	if (!raw) return;
	failedCategorySubImgKeys[categorySubImgFailKey(sub, item, firstIndex, i, j)] = true;
}

// 空状态组件（模板内复用）
const EmptyState = defineComponent({
	render: () =>
		h('div', { class: 'imgKongBox' }, [
			h('div', { class: 'categoryKong' }, [h('img', { src: '/staticSeo/images/shouyingkong.png', alt: '' })]),
			h('div', { class: 'kongTitle text-center' }, '抱歉，暂无分类数据！'),
		]),
});

// ─── Props & 路由 ──────────────────────────────────────
const props = defineProps({
	initialCompanyId: { type: [Number, String], default: 1 },
	isChildSite: { type: Boolean, default: false },
	initialProductnature: { type: String, default: null },
});

const route = useRoute();
const companyId = ref(Number(props.initialCompanyId) || 1);
const categoryPageStateKey = computed(() => `category-page-state:${props.isChildSite ? companyId.value : 1}`);
const categoryPageState = useState(categoryPageStateKey.value, () => ({
	productnature: props.initialProductnature ?? null,
	selectedCategory: null,
	leftScrollTop: 0,
	rightScrollTop: 0,
}));
const initialProductnature = categoryPageState.value?.productnature ?? props.initialProductnature ?? null;
const categoryProductnature = useCategoryProductnature(initialProductnature);

// ─── 响应式状态 ────────────────────────────────────────
const footerType = computed(() => (props.isChildSite || Number(props.initialCompanyId) !== 1 ? 'child' : 'main'));

const initIndex = NATURE_INDEX[initialProductnature] ?? 0;
const currentVTabs = ref(initIndex);
const leftCategories = ref([]); // 左侧一级分类
const rightCategories = ref([]); // 右侧子分类（选中一级后的二三级）
const selectedCategory = ref(null);
const kongFlag = ref(false);
const treeTabRef = ref(null);
const rightTabRef = ref(null);
const leftTabScrollRef = ref(null);
const searchHref = computed(() => {
	const path = buildCategorySearchPath(props.isChildSite ? companyId.value : 1);
	const query = new URLSearchParams({
		placeholders: '产品名称/型号规格/厂家/商品描述',
		companyId: normalizeCompanyId(props.isChildSite ? companyId.value : 1),
		...(params.productnature ? { productnature: params.productnature } : {}),
	});
	return `${path}?${query.toString()}`;
});
const tabHrefs = computed(() => TABS.map((_, index) => buildCategoryPageHref(NATURE_MAP[index] ?? null)));

const params = reactive({
	pageNum: 1,
	pageSize: 10,
	productnature: NATURE_MAP[initIndex] ?? null,
	firstcategory: null,
	filterConditions: [],
	ranking: '',
	searchName: '',
	companyId: String(companyId.value),
	isAttach: companyId.value === 1,
});

function syncCategoryPageState() {
	categoryPageState.value = {
		productnature: params.productnature ?? null,
		selectedCategory: selectedCategory.value ?? null,
		leftScrollTop: leftTabScrollRef.value?.scrollTop ?? 0,
		rightScrollTop: rightTabRef.value?.scrollTop ?? 0,
	};
}

// ─── URL 同步 ──────────────────────────────────────────
/**
 * 用 history.replaceState 静默更新地址栏，不触发 Vue Router 导航。
 * 如果用 router.replace，Nuxt 会因 path 变化重新挂载页面组件，
 * 导致 changeTab 的 async 回调写入已销毁的旧实例 ref。
 */
function updateUrl(productnature) {
	if (!import.meta.client) return;
	const target = buildCategoryPageHref(productnature);
	if (window.location.pathname !== target) {
		window.history.replaceState(null, '', target);
	}
}

/** 生成分类导航页地址，供顶部标签和路由同步复用。
 * @param {string | null | undefined} productnature 分类性质编码
 * @return {string} 不带参数的分类导航页路径
 */
function buildCategoryPageHref(productnature) {
	return buildCategoryPath(props.isChildSite ? companyId.value : 1, productnature);
}

// ─── Tab 切换 ──────────────────────────────────────────
/** 顶部 Tab 切换：更新状态 + URL + 请求数据 */
async function changeTab(index) {
	params.productnature = NATURE_MAP[index] ?? null;
	categoryProductnature.value = params.productnature;
	params.firstcategory = null;
	selectedCategory.value = null;
	categoryPageState.value = {
		productnature: params.productnature ?? null,
		selectedCategory: null,
		leftScrollTop: 0,
		rightScrollTop: 0,
	};
	updateUrl(params.productnature);
	const data = await fetchCategoryData();
	renderView(data);
}

// ─── 导航 ──────────────────────────────────────────────
/** 点击三级分类 → 产品列表 */
function toProductList(item, parentItem) {
	const path = buildCategoryDetailPath(companyId.value, item?.categoryId);
	navigateTo(path);
}

/** 生成跳转商品列表的 URL，用于 <a href> 与 rel="nofollow" */
function getProductListHref(item, parentItem) {
	return buildCategoryDetailPath(companyId.value, item?.categoryId);
}

/** 搜索框 → 搜索页（顶部搜索不传 firstcategory） */
function nextSearch() {
	navigateTo({
		path: buildCategorySearchPath(props.isChildSite ? companyId.value : 1),
		query: {
			placeholders: '产品名称/型号规格/厂家/商品描述',
			companyId: normalizeCompanyId(props.isChildSite ? companyId.value : 1),
			...(params.productnature ? { productnature: params.productnature } : {}),
		},
	});
}

// ─── 左侧树选中 ───────────────────────────────────────
function orderTap(item, index) {
	if (!item) return;
	params.firstcategory = item.name;
	selectedCategory.value = item.name;
	rightCategories.value = item.subCategory || [];
	rightTabRef.value?.scrollTo({ top: 0 });
	syncCategoryPageState();
}

function isLeftCategoryVisible(item) {
	return item?.name === selectedCategory.value;
}

// ─── 数据处理 ──────────────────────────────────────────
/**
 * 解析接口数据 → 设置左侧树 + 自动选中 + 右侧子分类。
 * 所有赋值必须同步完成（不能放 nextTick），确保 SSR/CSR 状态一致。
 */
function renderView(resData) {
	if (!resData || typeof resData !== 'object') {
		leftCategories.value = [];
		rightCategories.value = [];
		kongFlag.value = true;
		return;
	}

	// 主站字段 productHomeCategoryList（categoryName / sub），子站字段 categories（name / subCategory）
	if (params.companyId === '1') {
		leftCategories.value = filterVisibleCategories(
			(resData.productHomeCategoryList || []).map(item => ({
				...item,
				name: item.categoryName,
				subCategory: item.sub,
			}))
		);
	} else {
		leftCategories.value = filterVisibleCategories(resData.categories || []);
	}
	kongFlag.value = leftCategories.value.length === 0;

	// 有左侧分类 → 自动选中（优先恢复之前选中的，否则选第一个）
	if (leftCategories.value.length > 0) {
		const preferredCategoryName = selectedCategory.value ?? categoryPageState.value?.selectedCategory ?? null;
		const idx = preferredCategoryName != null ? leftCategories.value.findIndex(item => item.name === preferredCategoryName) : -1;
		const targetIndex = idx >= 0 ? idx : 0;
		const targetItem = leftCategories.value[targetIndex];

		if (targetItem) {
			params.firstcategory = targetItem.name;
			selectedCategory.value = targetItem.name;
			rightCategories.value = targetItem.subCategory || [];
		} else {
			rightCategories.value = [];
		}
		nextTick(() => {
			treeTabRef.value?.tab(targetItem, targetIndex);
			if (leftTabScrollRef.value) {
				leftTabScrollRef.value.scrollTop = categoryPageState.value?.leftScrollTop ?? 0;
			}
			if (rightTabRef.value) {
				rightTabRef.value.scrollTop = categoryPageState.value?.rightScrollTop ?? 0;
			}
		});
	} else {
		rightCategories.value = [];
	}
}

/** 提取分类商品数量：只有接口显式返回数量字段时才参与前台过滤。
 * @param {Record<string, any>} item 分类节点
 * @return {number | null} 分类数量；未返回时返回 null
 */
function getCategoryProductNum(item) {
	if (!item || typeof item !== 'object') return null;
	const productNum = item.productNum;
	if (typeof productNum === 'number') return productNum;
	if (typeof productNum === 'string' && productNum !== '' && !Number.isNaN(Number(productNum))) {
		return Number(productNum);
	}
	return null;
}

/** 过滤商品数量为 0 的分类，并保留其余分类层级用于页面渲染。
 * @param {Array} list 分类列表
 * @return {Array} 过滤后的分类列表
 */
function filterVisibleCategories(list) {
	return (Array.isArray(list) ? list : [])
		.map(item => ({
			...item,
			subCategory: filterVisibleCategories(item?.subCategory || []),
		}))
		.filter(item => {
			const productNum = getCategoryProductNum(item);
			return productNum == null || productNum > 0;
		});
}

// ─── 数据请求（SSR + CSR） ─────────────────────────────
/**
 * 失败返回 { _empty: true } 而非 null，
 * 防止 Nuxt 在客户端重复请求（falsy 返回值会触发 hydration 期间自动重试）
 */
async function fetchCategoryData() {
	params.companyId = String(companyId.value);
	params.isAttach = params.companyId === '1';
	params.filterConditions = [
		...(params.firstcategory != null ? [{ key: 'firstcategory', value: params.firstcategory }] : []),
		...(params.productnature != null ? [{ key: 'productcategories', value: params.productnature }] : []),
	];

	try {
		const res = await getMobileCategoryFromSearch(toRaw(params));
		if (Number(res?.code) === 200 && res.data) return res.data;
	} catch (e) {
		console.error('[category] fetch error:', e);
	}
	return { _empty: true };
}

// useAsyncData 仅负责 SSR 首屏请求，客户端切换直接调 fetchCategoryData
const { data: ssrCategoryData, pending: categoryPending } = await useAsyncData(
	`category-data-${companyId.value}-${props.initialProductnature}`,
	fetchCategoryData,
	{
		server: true,
		lazy: true,
	}
);
if (ssrCategoryData.value) {
	renderView(ssrCategoryData.value);
}
watch(ssrCategoryData, val => {
	if (val) renderView(val);
});

// 浏览器前进/后退 → prop 变化 → 更新 Tab + 请求数据
// guard: changeTab 已同步设置 params.productnature，此处跳过避免重复请求
watch(
	() => props.initialProductnature,
	async val => {
		const nature = NATURE_MAP[NATURE_INDEX[val] ?? 0] ?? null;
		categoryProductnature.value = nature;
		if (params.productnature === nature) return;
		const index = NATURE_INDEX[val] ?? 0;
		currentVTabs.value = index;
		params.productnature = nature;
		params.firstcategory = null;
		selectedCategory.value = null;
		const data = await fetchCategoryData();
		renderView(data);
	}
);

onBeforeUnmount(() => {
	syncCategoryPageState();
});
</script>

<style lang="scss" scoped>
:deep(::-webkit-scrollbar) {
	display: none;
	width: 0 !important;
	height: 0 !important;
	-webkit-appearance: none;
	background: transparent;
}

.category-loading {
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
}

.category-page {
	height: 100vh;
	display: flex;
	flex-direction: column;
	background: #f0f2f5;
	overflow: hidden;
}

.categoryBox {
	display: flex;
	flex: 1;
	min-height: 0;
	background: #f0f2f5;
	padding-top: 2px;
	padding-bottom: calc(4.17rem + env(safe-area-inset-bottom));
}

.categoryBox-inner {
	display: flex;
	flex: 1;
	min-height: 0;
	min-width: 0;
}

.rightTab {
	flex: 1;
	min-width: 0;
	overflow-y: auto;
	scroll-behavior: smooth;
}

.categoryBanner {
	background-color: #fff;
	padding: 8px 10px;

	.categoryTitle {
		display: flex;
		align-items: center;
		span {
			width: 12rem;
			font-size: 14px;
			font-weight: 500;
			color: #333333;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}

	.categoryListLeftBox {
		background-color: #fff;
		display: flex;
		flex-wrap: wrap;
		.categoryList {
			flex-basis: 33.3%;
			box-sizing: border-box;
			padding-top: 0.6rem;
			text-align: center;
			display: flex;
			flex-direction: column;
			align-items: center;
			cursor: pointer;
			text-decoration: none;
			color: inherit;

			.category-img {
				display: block;
				width: 6.5rem;
				height: 6.5rem;
				object-fit: cover;
				border-radius: 3px;
				margin-bottom: 0.42rem;
			}

			.categoryListTitle {
				width: 100%;
				text-align: center;
				font-size: 13px;
				font-family: NotoSansSC-Regular, NotoSansSC;
				font-weight: 400;
				color: #3d3d3d;
				line-height: 1.5rem;
				display: -webkit-box;
				overflow: hidden;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				line-clamp: 2;
			}
		}
	}
}

.top-head {
	display: flex;
	align-items: center;
	background-color: #fff;
	height: 3.67rem;
	padding: 0 12px;
	.back-icon {
		cursor: pointer;
		flex-shrink: 0;
	}
}

.search-head-cell {
	padding-left: 12px;
	flex: 1;
	min-width: 0;
}

.category-search-link {
	border: 1.5px solid #455bd9;
	border-radius: 7px;
	align-items: center;
	display: flex;
	height: 34px;
	padding: 2px 1px 2px 2px;
	background: #fff;
	text-decoration: none;
}

.category-search-placeholder {
	color: #595959;
	font-size: 12px;
	flex: 1;
	padding-left: 10px;
	padding-right: 10px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.category-search-button {
	border-radius: 5px;
	width: 60px;
	height: 30px;
	background: #455bd9;
	font-size: 13px;
	font-weight: 500;
	color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	white-space: nowrap;
}

.categoryHeadBox {
	background-color: #fff;
	padding: 0.21rem 0 0;
	margin-bottom: 1px;
	position: sticky;
	top: 0;
	z-index: 10;
}

.treeTabs {
	width: 8.4rem;
	flex-shrink: 0;
	overflow-y: auto;
	scroll-behavior: smooth;
}

.categoryListBox {
	background: #fff;
	height: 100%;
}

.imgKongBox {
	margin: 0 auto;
	background: #fff;
	.kongTitle {
		font-size: 1.17rem;
		font-family: NotoSansSC-Regular, NotoSansSC;
		font-weight: 400;
		color: #606266;
		line-height: 1.67rem;
		text-align: center;
		margin-bottom: 1.67rem;
	}

	.categoryKong {
		width: 18.67rem;
		height: 14.67rem;
		margin: 0 auto;
		img {
			width: 100%;
			height: 100%;
		}
	}
}
</style>
